import React, { useMemo, useState } from "react";
import { Card, Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { TeacherProgramProgress } from "../../../services/teacherProgress/teacherProgress.types";
import {
  ALL_GRADES,
  ALL_SUBJECTS,
  APP_LABELS,
  GradeFilter,
  SubjectFilter,
  buildGradeOptions,
  buildSubjectOptions,
  filterPrograms,
  formatLastViewedAt,
  formatWeekLabel,
  programsNeedingTitle,
} from "../../../utils/teacherProgress/teacher-progress-display";
import styles from "./styles.module.scss";

interface Props {
  programs: TeacherProgramProgress[];
}

const ProgramsCard: React.FC<Props> = ({ programs }) => {
  const [grade, setGrade] = useState<GradeFilter>(ALL_GRADES);
  const [subject, setSubject] = useState<SubjectFilter>(ALL_SUBJECTS);

  const gradeOptions = useMemo(() => buildGradeOptions(programs), [programs]);
  const subjectOptions = useMemo(
    () => buildSubjectOptions(programs),
    [programs],
  );
  const titled = useMemo(() => programsNeedingTitle(programs), [programs]);
  const rows = useMemo(
    () => filterPrograms(programs, grade, subject),
    [programs, grade, subject],
  );

  const columns: ColumnsType<TeacherProgramProgress> = [
    {
      title: "App",
      dataIndex: "app",
      key: "app",
      render: (app: TeacherProgramProgress["app"]) => APP_LABELS[app],
    },
    {
      title: "Grado",
      key: "grade",
      render: (_, program) => program.grade.name,
    },
    {
      title: "Asignatura",
      key: "subject",
      render: (_, program) => (
        <>
          {program.subject.name}
          {titled.has(program.programId) && (
            <small className={styles.programTitle}>
              {program.programTitle}
            </small>
          )}
        </>
      ),
    },
    {
      title: "Última semana vista",
      dataIndex: "week",
      key: "week",
      render: (week: string) => (
        <Tag color="purple">{formatWeekLabel(week)}</Tag>
      ),
    },
    {
      title: "Último acceso",
      dataIndex: "lastViewedAt",
      key: "lastViewedAt",
      render: (lastViewedAt: string) => formatLastViewedAt(lastViewedAt),
    },
  ];

  return (
    <Card
      title="Programas consultados"
      extra={
        <div className={styles.filters}>
          <Select
            aria-label="Grado"
            style={{ width: 200 }}
            value={grade}
            onChange={setGrade}
            options={gradeOptions}
          />
          <Select
            aria-label="Asignatura"
            style={{ width: 220 }}
            value={subject}
            onChange={setSubject}
            options={subjectOptions}
          />
        </div>
      }
    >
      <Table
        rowKey="programId"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: true }}
        locale={{
          emptyText:
            programs.length === 0
              ? "Este profesor todavía no abrió ningún programa."
              : "Ningún programa coincide con los filtros.",
        }}
      />
      <small className={styles.note}>
        Solo se muestran programas que el profesor ha abierto al menos una vez.
      </small>
    </Card>
  );
};

export default ProgramsCard;
